import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Download, Loader2, Ticket, Search, QrCode, LifeBuoy, RefreshCw, AlertTriangle, Mail, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin tab that lists every issued ticket for the selected event so admins
 * can see who bought what (name / email / phone / tier / price / qty).
 *
 * Uses GET /api/admin/events/{event_id}/tickets and offers a CSV download
 * that mirrors the shape of the guest-list export.
 *
 * Props:
 *   events  — [{id, title, date, venue}, …]   from the parent Admin dashboard
 *   token   — JWT
 */
export default function TicketsPanel({ events, token }) {
  const [selectedEventId, setSelectedEventId] = useState('');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Auto-select the first event on mount so the panel isn't empty.
  useEffect(() => {
    if (!selectedEventId && events?.length) {
      setSelectedEventId(events[0].id);
    }
  }, [events, selectedEventId]);

  const [ticketsSummary, setTicketsSummary] = useState({
    refunded_count: 0,
    refunded_amount: 0,
    gross_amount: 0,
    net_amount: 0,
    active_count: 0,
  });

  useEffect(() => {
    if (!selectedEventId || !token) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/admin/events/${selectedEventId}/tickets`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        if (!cancelled) {
          setTickets(r.data.tickets || []);
          setTicketsSummary({
            refunded_count: r.data.refunded_count || 0,
            refunded_amount: r.data.refunded_amount || 0,
            gross_amount: r.data.gross_amount || 0,
            net_amount: r.data.net_amount || 0,
            active_count: r.data.active_count || 0,
          });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setTickets([]);
          toast.error(err.response?.data?.detail || 'Could not load tickets');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedEventId, token]);

  const reloadTickets = useCallback(async () => {
    if (!selectedEventId) return;
    try {
      const r = await axios.get(`${API}/admin/events/${selectedEventId}/tickets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTickets(r.data.tickets || []);
      setTicketsSummary({
        refunded_count: r.data.refunded_count || 0,
        refunded_amount: r.data.refunded_amount || 0,
        gross_amount: r.data.gross_amount || 0,
        net_amount: r.data.net_amount || 0,
        active_count: r.data.active_count || 0,
      });
    } catch (_) { /* handled by toast in caller if needed */ }
  }, [selectedEventId, token]);

  const [syncingRefunds, setSyncingRefunds] = useState(false);
  const handleSyncRefunds = async () => {
    if (!selectedEventId) return;
    setSyncingRefunds(true);
    try {
      const res = await axios.post(
        `${API}/admin/events/${selectedEventId}/sync-refunds`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = res.data;
      if ((d.seats_refunded || 0) === 0) {
        const skipped = d.payments_skipped_local || 0;
        const scanned = d.payments_scanned || 0;
        toast.info(
          `Scanned ${scanned} Razorpay payment${scanned === 1 ? '' : 's'} — no new refunds to apply.` +
          (skipped ? ` (${skipped} internal test id${skipped === 1 ? '' : 's'} skipped.)` : '')
        );
      } else {
        toast.success(`Applied ${d.seats_refunded} refund${d.seats_refunded === 1 ? '' : 's'} (₹${(d.amount_refunded || 0).toLocaleString('en-IN')})`);
      }
      if ((d.errors || []).length) {
        const sample = (d.errors_sample || [])[0] || 'unknown error';
        toast.warning(
          `${d.errors.length} Razorpay lookup${d.errors.length === 1 ? '' : 's'} failed — e.g. "${String(sample).slice(0, 80)}"`
        );
      }
      await reloadTickets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncingRefunds(false);
    }
  };

  const handleExport = async () => {
    await exportCsv('export.csv', 'tickets');
  };

  // Organizer-safe export — strips Razorpay IDs, platform fee, and GST so
  // the file is safe to hand off to the venue partner / event organiser.
  const handleExportOrganizer = async () => {
    await exportCsv('export-organizer.csv', 'organizer');
  };

  const [sendingCsv, setSendingCsv] = useState(false);
  const handleSendOrganizerCsv = async () => {
    if (!selectedEventId) return;
    const force = window.confirm(
      "Email the organiser CSV NOW to the address saved on this event?\n\nClick OK to send. If it's already been sent automatically, this will resend."
    );
    if (!force) return;
    setSendingCsv(true);
    try {
      const res = await axios.post(
        `${API}/admin/events/${selectedEventId}/send-organizer-csv?force=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = res.data;
      if (d.status === 'sent') {
        toast.success(`CSV emailed to ${d.to}`);
      } else if (d.status === 'skipped') {
        toast.info(`Skipped: ${d.reason}`);
      } else {
        toast.error(d.reason || 'Send failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setSendingCsv(false);
    }
  };

  const exportCsv = async (endpoint, filenamePrefix) => {
    try {
      const res = await axios.get(
        `${API}/admin/events/${selectedEventId}/tickets/${endpoint}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const evt = events.find((e) => e.id === selectedEventId);
      a.download = `${filenamePrefix}-${(evt?.title || 'event').replace(/[^a-z0-9]/gi, '-')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handleDownloadQr = async (ticket) => {
    try {
      const res = await axios.get(`${API}/tickets/${ticket.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (ticket.attendee_name || 'guest').replace(/[^a-z0-9]/gi, '-').toLowerCase();
      a.download = `unight-ticket-${safeName}-${ticket.id.slice(0, 6)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Ticket PDF downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not download ticket');
    }
  };

  const [recovering, setRecovering] = useState(false);
  const [recoverIds, setRecoverIds] = useState(new Set());
  const recoverById = useCallback(async (identifier) => {
    const trimmed = identifier.trim();
    if (!trimmed.startsWith('pay_') && !trimmed.startsWith('order_')) {
      toast.error('Must start with pay_ or order_');
      return null;
    }
    const param = trimmed.startsWith('pay_') ? `payment_id=${trimmed}` : `order_id=${trimmed}`;
    const res = await axios.post(
      `${API}/admin/checkout/recover?${param}`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return res.data;
  }, [token]);

  const handleRecoverPayment = async () => {
    const input = window.prompt(
      'Paste the Razorpay payment_id (starts with pay_...) or order_id (starts with order_...) to recover:'
    );
    if (!input) return;
    setRecovering(true);
    try {
      const d = await recoverById(input);
      if (!d) return;
      const n = (d.ticket_ids || []).length;
      if (d.status === 'already_processed') {
        toast.info(`Order already fulfilled — ${n} tickets on record.`);
      } else {
        toast.success(`Recovered ${n} ticket${n === 1 ? '' : 's'} + emails dispatched.`);
      }
      // Refresh the tickets list for the current event
      if (selectedEventId) {
        const r = await axios.get(`${API}/admin/events/${selectedEventId}/tickets`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTickets(r.data.tickets || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recovery failed');
    } finally {
      setRecovering(false);
    }
  };

  // ============ Reconciliation ============
  const [reconLoading, setReconLoading] = useState(false);
  const [reconData, setReconData] = useState(null);
  const loadReconciliation = useCallback(async () => {
    setReconLoading(true);
    try {
      const res = await axios.get(`${API}/admin/checkout/reconciliation?lookback_days=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReconData(res.data);
      if (res.data.orphan_count === 0) {
        toast.success('No orphaned payments — all clear.');
      } else {
        toast.warning(`${res.data.orphan_count} orphaned payment${res.data.orphan_count === 1 ? '' : 's'} found — recover below.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not run reconciliation');
    } finally {
      setReconLoading(false);
    }
  }, [token]);

  const handleRecoverOrphan = async (orphan) => {
    const id = orphan.razorpay_payment_id || orphan.razorpay_order_id;
    if (!id) return;
    setRecoverIds((prev) => new Set(prev).add(id));
    try {
      const d = await recoverById(id);
      const n = (d?.ticket_ids || []).length;
      toast.success(`Recovered ${n} ticket${n === 1 ? '' : 's'} for ${orphan.buyer_email || orphan.razorpay_order_id}`);
      // Remove from local orphan list
      setReconData((prev) => prev && ({
        ...prev,
        orphans: prev.orphans.filter((o) => (o.razorpay_payment_id || o.razorpay_order_id) !== id),
        orphan_count: Math.max(0, prev.orphan_count - 1),
      }));
      // Refresh tickets list
      if (selectedEventId) {
        const r = await axios.get(`${API}/admin/events/${selectedEventId}/tickets`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTickets(r.data.tickets || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recovery failed');
    } finally {
      setRecoverIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const filtered = search
    ? tickets.filter((t) => {
        const q = search.toLowerCase();
        return (
          (t.attendee_name || '').toLowerCase().includes(q) ||
          (t.attendee_email || '').toLowerCase().includes(q) ||
          (t.attendee_phone || '').includes(q) ||
          (t.tier_name || '').toLowerCase().includes(q)
        );
      })
    : tickets;

  const totalRevenue = filtered.reduce((acc, t) => acc + (t.total_price_per_ticket || 0), 0);

  return (
    <div data-testid="admin-tickets-panel">
      {/* Event picker + export */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <select
          value={selectedEventId}
          onChange={(e) => setSelectedEventId(e.target.value)}
          className="flex-1 h-11 bg-[#050505] border border-zinc-800 focus:border-[#B5FF00] focus:outline-none text-white px-3 font-mono text-sm"
          data-testid="tickets-event-select"
        >
          {events.length === 0 && <option value="">No events yet</option>}
          {events.map((e) => (
            <option key={e.id} value={e.id}>
              {e.title} · {e.date}
            </option>
          ))}
        </select>
        <button
          onClick={handleExport}
          disabled={!selectedEventId || tickets.length === 0}
          className="border border-[#B5FF00] text-[#B5FF00] hover:bg-[#B5FF00] hover:text-black transition-colors px-4 h-11 font-mono text-xs tracking-widest disabled:opacity-40 flex items-center gap-2"
          data-testid="tickets-export-csv-btn"
        >
          <Download size={14} /> EXPORT CSV
        </button>
        <button
          onClick={handleExportOrganizer}
          disabled={!selectedEventId || tickets.length === 0}
          title="Organiser-safe CSV — hides Razorpay IDs, platform fee, and GST"
          className="border border-zinc-700 text-zinc-300 hover:border-[#B5FF00] hover:text-[#B5FF00] transition-colors px-4 h-11 font-mono text-xs tracking-widest disabled:opacity-40 flex items-center gap-2"
          data-testid="tickets-export-organizer-csv-btn"
        >
          <Download size={14} /> ORGANIZER CSV
        </button>
        <button
          onClick={handleSendOrganizerCsv}
          disabled={!selectedEventId || sendingCsv}
          title="Email the organiser CSV NOW to the address saved on this event"
          className="border border-zinc-700 text-zinc-300 hover:border-[#B5FF00] hover:text-[#B5FF00] transition-colors px-4 h-11 font-mono text-xs tracking-widest disabled:opacity-40 flex items-center gap-2"
          data-testid="tickets-email-organizer-btn"
        >
          {sendingCsv ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} EMAIL ORGANIZER
        </button>
        <button
          onClick={handleSyncRefunds}
          disabled={!selectedEventId || syncingRefunds}
          title="Pull refunds from Razorpay and update ticket counts + revenue"
          className="border border-zinc-700 text-zinc-300 hover:border-[#B5FF00] hover:text-[#B5FF00] transition-colors px-4 h-11 font-mono text-xs tracking-widest disabled:opacity-40 flex items-center gap-2"
          data-testid="tickets-sync-refunds-btn"
        >
          {syncingRefunds ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />} SYNC REFUNDS
        </button>
        <button
          onClick={handleRecoverPayment}
          disabled={recovering}
          title="Reissue tickets + emails for a Razorpay payment that didn't sync (e.g. during a redeploy)"
          className="border border-zinc-700 text-zinc-300 hover:border-[#B5FF00] hover:text-[#B5FF00] transition-colors px-4 h-11 font-mono text-xs tracking-widest disabled:opacity-40 flex items-center gap-2"
          data-testid="tickets-recover-payment-btn"
        >
          {recovering ? <Loader2 size={14} className="animate-spin" /> : <LifeBuoy size={14} />} RECOVER PAYMENT
        </button>
        <button
          onClick={loadReconciliation}
          disabled={reconLoading}
          title="Scan the last 30 days on Razorpay for payments that captured but never issued tickets"
          className="border border-zinc-700 text-zinc-300 hover:border-[#B5FF00] hover:text-[#B5FF00] transition-colors px-4 h-11 font-mono text-xs tracking-widest disabled:opacity-40 flex items-center gap-2"
          data-testid="tickets-recon-scan-btn"
        >
          {reconLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} SCAN ORPHANS
        </button>
      </div>

      {/* Reconciliation report */}
      {reconData && (
        <div className="border border-zinc-800 bg-[#050505] mb-6" data-testid="tickets-recon-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
            <div className="flex items-center gap-2">
              <AlertTriangle size={14} className={reconData.orphan_count > 0 ? 'text-yellow-500' : 'text-zinc-500'} />
              <span className="font-mono text-xs tracking-widest text-white">
                RECONCILIATION · {reconData.checked_count} orders scanned · {reconData.orphan_count} orphans · {reconData.unpaid_stale_count} abandoned
              </span>
            </div>
            <button
              onClick={() => setReconData(null)}
              className="font-mono text-[10px] tracking-widest text-zinc-500 hover:text-white"
              data-testid="tickets-recon-close-btn"
            >
              CLOSE
            </button>
          </div>

          {reconData.orphan_count === 0 ? (
            <div className="px-4 py-6 text-center font-mono text-xs text-zinc-500">
              No orphaned payments in the last {reconData.lookback_days} days. All Razorpay captures reconciled.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0A0A0A] border-b border-zinc-800 font-mono text-[10px] text-zinc-500 tracking-widest">
                  <tr>
                    <th className="text-left px-3 py-2">RAZORPAY ORDER</th>
                    <th className="text-left px-3 py-2">PAYMENT</th>
                    <th className="text-left px-3 py-2">BUYER</th>
                    <th className="text-left px-3 py-2">EVENT · TIER</th>
                    <th className="text-right px-3 py-2">AMOUNT ₹</th>
                    <th className="text-right px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-900" data-testid="tickets-recon-tbody">
                  {reconData.orphans.map((o) => {
                    const id = o.razorpay_payment_id || o.razorpay_order_id;
                    const isRecovering = recoverIds.has(id);
                    return (
                      <tr key={id} className="hover:bg-[#0A0A0A]" data-testid={`recon-orphan-${o.razorpay_order_id}`}>
                        <td className="px-3 py-2 font-mono text-[11px] text-zinc-300">{o.razorpay_order_id}</td>
                        <td className="px-3 py-2 font-mono text-[11px] text-zinc-300">{o.razorpay_payment_id || '—'}</td>
                        <td className="px-3 py-2">
                          <div className="text-white text-xs">{o.buyer_name || '—'}</div>
                          <div className="text-zinc-500 text-[10px] font-mono">{o.buyer_email || '—'}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-white text-xs">{o.event_title || '—'}</div>
                          <div className="text-[#B5FF00] text-[10px] font-mono">{o.tier_name} × {o.quantity}</div>
                        </td>
                        <td className="px-3 py-2 text-right text-white font-mono">
                          ₹{(o.amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => handleRecoverOrphan(o)}
                            disabled={isRecovering}
                            className="border border-[#B5FF00] text-[#B5FF00] hover:bg-[#B5FF00] hover:text-black transition-colors px-3 py-1 font-mono text-[10px] tracking-widest disabled:opacity-40 inline-flex items-center gap-1.5"
                            data-testid={`recon-recover-btn-${o.razorpay_order_id}`}
                          >
                            {isRecovering ? <Loader2 size={11} className="animate-spin" /> : <LifeBuoy size={11} />} RECOVER
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <div className="border border-zinc-800 bg-[#050505] p-3">
          <div className="font-mono text-[10px] text-zinc-500 tracking-widest">TICKETS SOLD</div>
          <div className="text-[#B5FF00] text-2xl font-bold" data-testid="stat-tickets-sold">
            {ticketsSummary.active_count || tickets.length}
            {ticketsSummary.refunded_count > 0 && (
              <span className="text-zinc-500 text-sm font-mono ml-2">
                (−{ticketsSummary.refunded_count} refunded)
              </span>
            )}
          </div>
        </div>
        <div className="border border-zinc-800 bg-[#050505] p-3">
          <div className="font-mono text-[10px] text-zinc-500 tracking-widest">UNIQUE BUYERS</div>
          <div className="text-white text-2xl font-bold">
            {new Set(tickets.filter((t) => !t.refunded_at).map((t) => t.attendee_email).filter(Boolean)).size}
          </div>
        </div>
        <div className="border border-zinc-800 bg-[#050505] p-3">
          <div className="font-mono text-[10px] text-zinc-500 tracking-widest">
            NET REVENUE ₹
          </div>
          <div className="text-[#B5FF00] text-2xl font-bold" data-testid="stat-net-revenue">
            {(ticketsSummary.net_amount || totalRevenue).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          </div>
          {ticketsSummary.refunded_amount > 0 && (
            <div className="font-mono text-[10px] text-zinc-500 mt-1" data-testid="stat-refunds-detail">
              Gross ₹{(ticketsSummary.gross_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              {' · '}
              Refunded ₹{(ticketsSummary.refunded_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </div>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="mb-3 relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name / email / phone / tier…"
          className="w-full h-10 pl-10 pr-4 bg-[#050505] border border-zinc-800 focus:border-[#B5FF00] focus:outline-none text-white text-sm font-mono"
          data-testid="tickets-search-input"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-zinc-500">
          <Loader2 className="animate-spin" size={24} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-zinc-800 p-12 text-center text-zinc-500 font-mono text-sm">
          <Ticket size={32} className="mx-auto mb-3 opacity-40" />
          {tickets.length === 0 ? 'No tickets sold yet for this event.' : 'No matches.'}
        </div>
      ) : (
        <div className="border border-zinc-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#050505] border-b border-zinc-800 font-mono text-[10px] text-zinc-500 tracking-widest">
              <tr>
                <th className="text-left px-3 py-3">NAME</th>
                <th className="text-left px-3 py-3">EMAIL</th>
                <th className="text-left px-3 py-3">PHONE</th>
                <th className="text-left px-3 py-3">TIER</th>
                <th className="text-right px-3 py-3">SEAT</th>
                <th className="text-right px-3 py-3">QTY (ORDER)</th>
                <th className="text-right px-3 py-3">PRICE ₹</th>
                <th className="text-left px-3 py-3">STATUS</th>
                <th className="text-left px-3 py-3">PURCHASED</th>
                <th className="text-right px-3 py-3">QR</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900" data-testid="tickets-tbody">
              {filtered.map((t) => {
                const isRefunded = !!t.refunded_at;
                return (
                <tr
                  key={t.id}
                  className={`hover:bg-[#0A0A0A] ${isRefunded ? 'opacity-60' : ''}`}
                  data-testid={`ticket-row-${t.id}`}
                >
                  <td className={`px-3 py-2.5 text-white ${isRefunded ? 'line-through' : ''}`}>{t.attendee_name || '—'}</td>
                  <td className={`px-3 py-2.5 text-zinc-300 font-mono text-xs ${isRefunded ? 'line-through' : ''}`}>{t.attendee_email || '—'}</td>
                  <td className="px-3 py-2.5 text-zinc-300 font-mono text-xs">{t.attendee_phone || '—'}</td>
                  <td className="px-3 py-2.5 text-[#B5FF00]">{t.tier_name || '—'}</td>
                  <td className="px-3 py-2.5 text-zinc-400 text-right font-mono">{t.seat_number || '—'}</td>
                  <td className="px-3 py-2.5 text-zinc-400 text-right font-mono">{t.quantity_per_order}</td>
                  <td className={`px-3 py-2.5 text-white text-right font-mono ${isRefunded ? 'line-through' : ''}`}>
                    ₹{(t.total_price_per_ticket || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </td>
                  <td className="px-3 py-2.5">
                    {isRefunded ? (
                      <span
                        className="text-[10px] font-mono tracking-widest px-2 py-0.5 border text-orange-400 border-orange-500/40 bg-orange-500/5"
                        data-testid={`ticket-refunded-badge-${t.id}`}
                      >
                        REFUNDED
                      </span>
                    ) : (
                      <span className={`text-[10px] font-mono tracking-widest px-2 py-0.5 border ${
                        t.used_at
                          ? 'text-zinc-400 border-zinc-700'
                          : 'text-[#B5FF00] border-[#B5FF00]/40 bg-[#B5FF00]/5'
                      }`}>
                        {t.used_at ? 'SCANNED' : (t.status || '').toUpperCase()}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-zinc-500 font-mono text-xs">
                    {t.purchased_at ? t.purchased_at.slice(0, 16).replace('T', ' ') : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button
                      onClick={() => handleDownloadQr(t)}
                      disabled={isRefunded}
                      title={isRefunded ? 'Ticket refunded — QR disabled' : 'Download ticket PDF with QR'}
                      className="inline-flex items-center gap-1.5 border border-[#B5FF00]/40 text-[#B5FF00] hover:bg-[#B5FF00] hover:text-black transition-colors px-2.5 py-1 font-mono text-[10px] tracking-widest disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#B5FF00]"
                      data-testid={`ticket-download-qr-btn-${t.id}`}
                    >
                      <QrCode size={12} /> QR
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
